/**
 * The versions inside a release, as a rail down the left of the release page.
 *
 * One row per version — number, status, date — newest first, the selected
 * one lifted out. Picking a row swaps the detail beside it; it never opens a
 * second page, because a version is a view of the release, not a place of
 * its own. Paginated at 25 like every list in the dashboard, though a real
 * release holds far fewer.
 */
import { Box, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Tag } from "lucide-react";
import type { JSX } from "react";

import { Pagination } from "../../../components/ui/pagination.js";
import { usePagedList } from "../../../hooks/usePagedList.js";
import { RELEASE_STATUS_COLOR, statusLabel } from "./release-status.js";
import type { ReleaseDTO } from "./releases-api.js";

interface Props {
  releaseVersion: string;
  versions: ReleaseDTO[];
  selectedId: number | undefined;
  onSelect: (version: ReleaseDTO) => void;
}

export function VersionRail({ releaseVersion, versions, selectedId, onSelect }: Props): JSX.Element {
  const { paged, page, perPage, totalCount, totalPages, setPage, setPerPage } =
    usePagedList(versions, 25);

  return (
    <Box component="nav" aria-label={`Versions of ${releaseVersion}`}>
      <Typography
        sx={{
          px: 1,
          mb: 1,
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "text.disabled",
        }}
      >
        Versions
      </Typography>
      <Box
        component="ul"
        data-testid={`release-versions-${releaseVersion}`}
        sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 0.5 }}
      >
        {paged.map((version) => {
          const selected = version.id === selectedId;
          return (
            <Box component="li" key={version.id}>
              <Box
                component="button"
                type="button"
                data-testid={`version-item-${version.version}`}
                aria-current={selected ? "true" : undefined}
                aria-label={`Show version ${version.version}`}
                onClick={() => onSelect(version)}
                sx={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  gap: 1,
                  textAlign: "left",
                  px: 1.25,
                  py: 1,
                  border: 1,
                  borderColor: selected ? "primary.main" : "transparent",
                  borderRadius: 1,
                  bgcolor: (t) => (selected ? alpha(t.palette.primary.main, 0.06) : "transparent"),
                  cursor: "pointer",
                  font: "inherit",
                  color: "inherit",
                  transition: "background-color 120ms ease, border-color 120ms ease",
                  "&:hover": {
                    bgcolor: (t) => alpha(t.palette.primary.main, selected ? 0.08 : 0.04),
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{ display: "inline-flex", color: selected ? "primary.main" : "text.disabled", flexShrink: 0 }}
                >
                  <Tag size={14} />
                </Box>
                <Box component="span" sx={{ display: "grid", minWidth: 0, flex: 1 }}>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: (t) => t.typography.h6.fontFamily,
                      fontSize: 14,
                      fontWeight: 600,
                      letterSpacing: "-0.01em",
                      color: "text.primary",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {version.version}
                  </Box>
                  <Box component="span" sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {version.release_date ?? "No date yet"}
                  </Box>
                </Box>
                <Chip
                  size="small"
                  variant={version.status === "released" ? "filled" : "outlined"}
                  color={RELEASE_STATUS_COLOR[version.status] ?? "default"}
                  label={statusLabel(version.status)}
                  sx={{ height: 20, flexShrink: 0 }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ mt: 1 }}>
        <Pagination
          page={page}
          perPage={perPage}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          entityNoun="versions"
          hideIfSinglePage
        />
      </Box>
    </Box>
  );
}
